/**
 * Safe date formatting utilities.
 *
 * These functions handle invalid/malformed date strings gracefully,
 * preventing "Invalid Date" from appearing in the UI.
 */

/**
 * Safely parse a date string, returning null if invalid.
 */
export function safeParseDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date;
}

/**
 * Safely format a date string using toLocaleString().
 */
export function safeFormatDate(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'Unknown'
): string {
  const date = safeParseDate(dateStr);
  if (!date) return fallback;
  return date.toLocaleString(undefined, options);
}

/**
 * Safely format a date string to local date only (no time).
 */
export function safeFormatDateOnly(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'Unknown'
): string {
  const date = safeParseDate(dateStr);
  if (!date) return fallback;
  return date.toLocaleDateString(undefined, options);
}

/**
 * Safely format a date string to time only.
 */
export function safeFormatTime(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'Unknown'
): string {
  const date = safeParseDate(dateStr);
  if (!date) return fallback;
  return date.toLocaleTimeString(undefined, options);
}

/**
 * Safely format a date string as relative time (Today, Yesterday, X days ago).
 */
export function safeFormatRelative(
  dateStr: string | null | undefined,
  fallback = 'Recently'
): string {
  const date = safeParseDate(dateStr);
  if (!date) return fallback;

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

/**
 * Safely get timestamp age in milliseconds, returning null if invalid.
 */
export function safeGetTimestampAge(dateStr: string | null | undefined): number | null {
  const date = safeParseDate(dateStr);
  if (!date) return null;
  return Date.now() - date.getTime();
}

/**
 * Safely format how long ago a timestamp was, at the granularity that answers "is this stale".
 *
 * safeFormatRelative is day-grained, so everything that happened since midnight reads "Today".
 * A run that last moved four hours ago and one that last moved four minutes ago are the two
 * cases an operator is trying to tell apart, so this keeps minutes and hours and only falls
 * back to a date once an age is no longer the useful fact.
 */
export function safeFormatAge(dateStr: string | null | undefined, fallback = 'Unknown'): string {
  const age = safeGetTimestampAge(dateStr);
  if (age === null) return fallback;

  // A clock skew between the browser and the backend can put a timestamp slightly ahead of now.
  // Reading that as "in 2 minutes" would make a run that just moved look scheduled.
  const minutes = Math.floor(Math.max(age, 0) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return safeFormatDateOnly(dateStr, { day: 'numeric', month: 'short' }, fallback);
}
